import { useState, useEffect } from 'react';
import { fetchWithAuth } from './authHelpers';
import { trackEvent } from '../analytics';

const useEvent = (event_id) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fantasyTeam, setFantasyTeam] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventResponse = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/events/hltv/${event_id}`, 'GET');
        const eventData = await eventResponse.json();
        setEvent(eventData);

        const fantasyTeamResponse = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/user/current/event/${eventData._id}`, 'GET');
        if (fantasyTeamResponse.ok) {
          const fantasyTeamData = await fantasyTeamResponse.json();
          setFantasyTeam(fantasyTeamData[0]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [event_id]);

  const handleSaveTeam = async (event_id, team) => {
    if (isSaving) {
      return; // Prevent multiple simultaneous saves
    }

    if (team.length < 5) {
      setSaveError('Please select 5 players');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      let response;

      if (fantasyTeam) {
        response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam/${fantasyTeam._id}`, 'PUT', {
          cards: team
        });
      } else {
        response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/fantasyteam`, 'POST', {
          eventHltvID: event_id,
          cards: team
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save fantasy team');
      }

      const updatedFantasyTeam = await response.json();
      setFantasyTeam(updatedFantasyTeam);
      await upsertFantasyToEvent(updatedFantasyTeam);

      // Add fantasy team to each new card
      const oldTeam = fantasyTeam ? fantasyTeam.cards.map(card => card.card._id) : [];
      const newTeam = team.map(card => card.card._id);

      const cardsToAdd = newTeam.filter(cardId => !oldTeam.includes(cardId));
      for (const cardID of cardsToAdd) {
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cards/${cardID}/addFantasyTeam`, 'PATCH', { fantasyTeam: updatedFantasyTeam._id });
      }

      // Remove fantasy team from cards not in the team
      if (fantasyTeam) {
        const cardsToRemove = oldTeam.filter(cardId => !newTeam.includes(cardId));
        for (const cardID of cardsToRemove) {
          await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cards/${cardID}/removeFantasyTeam`, 'PATCH', { fantasyTeam: updatedFantasyTeam._id });
        }
      }

      setSaveError(null);
      setSaveSuccess('Fantasy team saved successfully!');
      trackEvent(fantasyTeam ? 'fantasy_team_updated' : 'fantasy_team_created', {
        event_id: event_id,
      });
    } catch (err) {
      setSaveSuccess(null);
      setSaveError(err.message);
      console.error("Error saving fantasy team");
    } finally {
      setIsSaving(false);
    }
  };

  const upsertFantasyToEvent = async (fantasyTeam) => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/events/${event._id}/upsertfantasy`, 'POST', { fantasyTeam });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add fantasy team to event');
      }
      setSaveError(null);
      setSaveSuccess('Fantasy team saved successfully!');
    } catch (err) {
      setSaveSuccess(null);
      setSaveError(err.message);
      console.error("Error saving fantasy team");
    }
  };

  const lockEvent = async (event_id) => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/events/${event_id}/lock`, 'PUT');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to lock event');
      }
    } catch (err) {
      console.error("Error locking event");
    }
  }

  return { event, loading, error, fantasyTeam, saveError, saveSuccess, isSaving, setFantasyTeam, handleSaveTeam, lockEvent };
};

export default useEvent;
